import { BaseWebSocketClient } from './baseWebSocketClient.js'

const buildGradeSnapshot = (student, rubricScores) => ({
  studentId: student.id,
  score: student.score,
  feedback: student.feedback,
  isComplete: student.is_complete === 1,
  rubricScores: { ...rubricScores },
})

const navigationDisposition = ({
  currentStudentId,
  targetStudentId,
  dirty,
  saving,
}) => {
  if (currentStudentId === targetStudentId) return 'same'
  if (saving) return 'blocked'
  if (dirty) return 'confirm'
  return 'navigate'
}

// Workflow-state helpers shared by every Alpine.data component on the page.
// Defined at module scope (rather than on the parent Alpine.data) because
// nested-component templates can't reliably resolve parent methods —
// so each child re-exposes these via thin wrappers below.
const isReviewed = (student) => {
  if (!student) return false
  if (student.is_saved) return true
  if ((student.score || 0) > 0) return true
  if ((student.feedback || '').trim()) return true
  if ((student.teacher_rubric_score?.criterion_scores || []).length > 0)
    return true
  return false
}

const statusFor = (student) => {
  if (!student) return 'unsubmitted'
  if (!student.is_submitted || student.is_missing) return 'unsubmitted'
  if (student.is_graded) return 'returned'
  return isReviewed(student) ? 'reviewed' : 'unreviewed'
}

document.addEventListener('alpine:init', () => {
  // Rubric filter state — shared by the header strip and the student-list
  // section headers/contextual actions; a store keeps both in sync.
  // Status filter for the student list. Values: 'all' | 'unsubmitted' | 'unreviewed' | 'reviewed' | 'returned'.
  Alpine.store('statusFilter', {
    value: 'all',
    set(v) {
      this.value = v
    },
    get isActive() {
      return this.value !== 'all'
    },
  })

  // Focus mode — global so the grading-page components AND the teacher nav
  // (separate Alpine root) can both react. No-op on pages that don't set it.
  Alpine.store('focusMode', {
    active: false,
    enter() {
      this.active = true
      // Mutually exclusive with insights mode (different layout swaps).
      Alpine.store('insightsMode')?.exit()
    },
    exit() {
      this.active = false
      // Don't leave the drawer flagged open while focus is off.
      Alpine.store('filtersDrawer')?.close()
    },
  })

  // Focus-mode drawer state for the otherwise-hidden filter panel.
  Alpine.store('filtersDrawer', {
    open: false,
    toggle() {
      this.open = !this.open
    },
    close() {
      this.open = false
    },
  })

  // Insights mode — replaces middle + right columns with a criterion analysis surface.
  Alpine.store('insightsMode', {
    active: false,
    enter() {
      this.active = true
      // Mutually exclusive with focus mode (different layout swaps).
      Alpine.store('focusMode').exit()
    },
    exit() {
      this.active = false
    },
  })

  // Global dropdown manager
  Alpine.store('dropdowns', {
    activeDropdown: null,

    open(id) {
      this.activeDropdown = id
    },

    close() {
      this.activeDropdown = null
    },

    isOpen(id) {
      return this.activeDropdown === id
    },
  })

  class TeacherWebSocketClient extends BaseWebSocketClient {
    constructor(assignmentId, alpineComponent = null) {
      super(assignmentId, alpineComponent)
    }

    getWebSocketUrl() {
      return `/ws/assignment/${this.assignmentId}/`
    }

    onMessage(data) {
      if (!this.alpineComponent) return

      switch (data.type) {
        case 'response_update':
          this.alpineComponent.updateResponseInState(data)
          if (data.node_id) {
            this.alpineComponent.updateResponseStatus(
              data.student_id,
              data.node_id,
              {
                is_submitted: data.is_submitted,
                submitted_at: new Date().toISOString(),
              }
            )
          }
          break

        case 'typing':
          this.alpineComponent.updateTypingState(data.student_id, {
            isTyping: true,
            studentName: data.student_name,
            timestamp: Date.now(),
          })
          if (data.node_id) {
            this.alpineComponent.updateResponseStatus(
              data.student_id,
              data.node_id,
              {
                is_typing: true,
                typing_timestamp: Date.now(),
              }
            )
          }
          break

        case 'stop_typing':
          this.alpineComponent.removeTypingState(data.student_id)
          this.alpineComponent.clearStudentResponseTyping(data.student_id)
          break

        case 'assignment_submitted':
          this.alpineComponent.updateStudentSubmissionStatus(data)
          break

        case 'pong':
          this.handlePong()
          break
      }
    }
  }

  // Main component for the assignment page
  Alpine.data('assignmentPage', (config) => ({
    state: {
      assignment: {
        id: config.assignment_id,
        grading_type: config.grading_type || 'points',
        details: null,
      },
      students: {
        list: [],
        loading: true,
        selectAll: false,
      },
      typing: {}, // Store typing indicators per student
      selectedStudent: {
        id: null,
        loading: false,
        ui: {
          error: null,
          success: null,
        },
      },
      showAllSections: false,
    },
    csrf_token: config.csrf_token,
    can_grade: config.can_grade,
    is_admin: config.is_admin,
    websocketClient: null,
    aiPollInterval: null,
    init() {
      this._forceNavigationHandler = (event) => {
        this.selectStudent(event.detail.studentId, { force: true })
      }
      window.addEventListener(
        'assignment:force-navigation',
        this._forceNavigationHandler
      )
      this.fetchStudentList()
      this.initializeWebSocket()
      this.initKeyboardShortcuts()
    },

    // ← / → switch students, Esc exits focus. Ignored while typing.
    initKeyboardShortcuts() {
      document.addEventListener('keydown', (e) => {
        // Cmd/Ctrl+S triggers unified Save — works even from inputs
        // so teachers can save without leaving the textarea.
        if ((e.metaKey || e.ctrlKey) && e.key === 's') {
          if (this.state.selectedStudent.id === null) return
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('assignment:save'))
          return
        }
        const el = e.target
        const tag = (el.tagName || '').toLowerCase()
        if (tag === 'input' || tag === 'textarea' || el.isContentEditable) {
          return
        }
        if (e.key === 'Escape') {
          // Drawer first, focus second — same Esc, layered dismiss.
          if (Alpine.store('filtersDrawer').open) {
            Alpine.store('filtersDrawer').close()
          } else if (Alpine.store('focusMode').active) {
            Alpine.store('focusMode').exit()
          }
          return
        }
        if (this.state.selectedStudent.id === null) return
        if (e.key === 'ArrowRight') {
          e.preventDefault()
          this.gotoAdjacentStudent(1)
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault()
          this.gotoAdjacentStudent(-1)
        }
      })
    },

    gotoAdjacentStudent(direction) {
      const list = this.state.students.list
      if (!list.length) return
      const i = list.findIndex((s) => s.id === this.state.selectedStudent.id)
      const next = (i + direction + list.length) % list.length
      this.selectStudent(list[next].id)
    },
    initializeWebSocket() {
      this.websocketClient = new TeacherWebSocketClient(
        this.state.assignment.id,
        this
      )
      // Exposed for cleanup on page unload (see beforeunload handler below).
      window.assignmentWebSocket = this.websocketClient
    },

    updateResponseInState(data) {
      const { student_id, response_id, content, node_id, timestamp } = data

      // Find the student in the state
      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === student_id
      )
      if (studentIndex !== -1) {
        // Find the response within the student's responses
        const responseIndex = this.state.students.list[
          studentIndex
        ].responses.findIndex((r) => r.id === response_id)

        if (responseIndex !== -1) {
          // Update the response content in Alpine state
          this.state.students.list[studentIndex].responses[
            responseIndex
          ].content = content

          // Add a visual indicator flag for recent updates
          this.state.students.list[studentIndex].responses[
            responseIndex
          ].recently_updated = true
          // Remove the visual indicator after 2 seconds
          setTimeout(() => {
            if (
              this.state.students.list[studentIndex] &&
              this.state.students.list[studentIndex].responses[responseIndex]
            ) {
              this.state.students.list[studentIndex].responses[
                responseIndex
              ].recently_updated = false
            }
          }, 2000)
        }
      }
    },

    updateTypingState(studentId, typingData) {
      // Method called directly by WebSocket client to update typing state
      this.state.typing[studentId] = typingData
    },

    removeTypingState(studentId) {
      // Method called directly by WebSocket client to remove typing state
      delete this.state.typing[studentId]
    },

    updateStudentSubmissionStatus(data) {
      // Method called directly by WebSocket client to update student submission status
      const { student_id, is_submitted } = data

      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === student_id
      )
      if (studentIndex !== -1) {
        const student = this.state.students.list[studentIndex]
        student.is_submitted = is_submitted !== undefined ? is_submitted : true
        student.submitted_at = new Date().toISOString()
        // Clear the retake flag locally on resubmit so the "Retake"
        // pill doesn't linger; the server also clears it in
        // StudentAssignment.save(), so a refetch agrees.
        if (student.is_submitted && student.is_retake) {
          student.is_retake = false
        }
      }
    },

    updateResponseStatus(studentId, nodeId, status) {
      // Method to update individual response status (submitted, typing, etc.)
      studentId = parseInt(studentId)
      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === studentId
      )
      if (studentIndex !== -1) {
        // Convert nodeId to number to match the response.node_id type
        const numericNodeId = parseInt(nodeId)

        const responseIndex = this.state.students.list[
          studentIndex
        ].responses.findIndex((r) => r.node_id === numericNodeId)

        if (responseIndex !== -1) {
          this.state.students.list[studentIndex].responses[responseIndex] = {
            ...this.state.students.list[studentIndex].responses[responseIndex],
            ...status,
          }
        }
      }
    },

    clearStudentResponseTyping(studentId) {
      // Clear typing state from all responses for a student
      studentId = parseInt(studentId)
      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === studentId
      )
      if (studentIndex !== -1) {
        this.state.students.list[studentIndex].responses.forEach((response) => {
          if (response.is_typing) {
            response.is_typing = false
            delete response.typing_timestamp
          }
        })
      }
    },
    returnSelectedStudent() {
      const assignment_ids = [this.state.selectedStudent.id]

      fetch(`/api/assignments/${this.state.assignment.id}/return/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({ assignment_ids: assignment_ids }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            data.assignment_ids.forEach((assignment_id) => {
              this.state.students.list.find(
                (s) => s.id === assignment_id
              ).is_graded = true
            })
            this.state.selectedStudent.ui.success =
              'Assignments returned to students'
            setTimeout(() => {
              this.state.selectedStudent.ui.success = null
            }, 3000)
          }
        })
        .catch((error) => {
          console.error('Error returning assignments:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error = 'Failed to return assignments'
          }
        })
    },

    nextSelectedStudent() {
      const currentIndex = this.state.students.list.findIndex(
        (s) => s.id === this.state.selectedStudent.id
      )
      const nextIndex = (currentIndex + 1) % this.state.students.list.length
      this.selectStudent(this.state.students.list[nextIndex].id)
    },

    toggleAllSections() {
      this.state.showAllSections = !this.state.showAllSections
      this.fetchStudentList()
    },

    // Fetch the list of students for this assignment
    fetchStudentList() {
      this.state.students.loading = true
      this.state.selectedStudent.ui.error = null

      const allSectionsParam = this.state.showAllSections
        ? '&all_sections=true'
        : ''
      const urlParams = new URLSearchParams(window.location.search)
      const sectionSlugParam = urlParams.get('section_slug')
        ? `&section_slug=${encodeURIComponent(urlParams.get('section_slug'))}`
        : ''
      fetch(
        `/api/assignments/${this.state.assignment.id}/students/?is_admin=${this.is_admin}${allSectionsParam}${sectionSlugParam}`
      )
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to view this assignment.'
            this.state.students.loading = false
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          // Process student data and add UI state properties
          this.state.students.list = (data.students || []).map((student) => ({
            ...student,
            selected: false,
            loaded: false,
            responses: [],
            feedback: '',
            ...data,
          }))

          this.state.students.loading = false

          // Select first student by default if available
          if (this.state.students.list.length > 0) {
            this.selectStudent(this.state.students.list[0].id)
          }
          const anyPending = this.state.students.list.some(
            (s) => s.ai_rubric_score?.status === 'pending'
          )
          if (anyPending) this.startAIPolling()
        })
        .catch((error) => {
          console.error('Error fetching student list:', error)
          this.state.students.loading = false
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to load student list. Please try again.'
          }
        })
    },

    // Select a student and load their assignment details
    selectStudent(studentId, { force = false } = {}) {
      if (
        !force &&
        window.assignmentGradingGuard &&
        !window.assignmentGradingGuard.requestNavigation(studentId)
      ) {
        return
      }
      this.state.selectedStudent.id = studentId
      const student = this.state.students.list.find((s) => s.id === studentId)
      if (student && student.loaded) {
        // Data already loaded, no need to fetch again
        return
      }

      this.fetchStudentAssignment(studentId)
    },

    // Fetch assignment details for a specific student. Returns the fetch
    // promise so callers (e.g. Insights → student transition) can await.
    fetchStudentAssignment(studentId) {
      this.state.selectedStudent.loading = true
      this.state.selectedStudent.ui.error = null

      return fetch(`/api/student-assignments/${studentId}/`)
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to view this assignment.'
            this.state.selectedStudent.loading = false
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          const studentIndex = this.state.students.list.findIndex(
            (s) => s.id === studentId
          )
          if (studentIndex !== -1) {
            this.state.students.list[studentIndex] = {
              ...this.state.students.list[studentIndex], // Keep existing properties
              ...data, // Add all properties from API
              responses: data.responses || [], // Store responses in the student object
              feedback: data.teacher_comments || '', // Store feedback in the student object
              loaded: true, // Mark as loaded
            }
          }

          this.state.selectedStudent.loading = false
          if (this.state.selectedStudent.id === studentId) {
            window.dispatchEvent(
              new CustomEvent('assignment:student-loaded', {
                detail: { studentId },
              })
            )
          }
        })
        .catch((error) => {
          console.error('Error fetching student assignment:', error)
          this.state.selectedStudent.loading = false
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to load student assignment. Please try again.'
          }
        })
    },
    // Helper getters for derived state
    get currentStudent() {
      return (
        this.state.students.list.find(
          (s) => s.id === this.state.selectedStudent.id
        ) || {}
      )
    },

    // True when any student has at least one rubric criterion score. Gates the Insights button.
    get hasAnyRubricScores() {
      return this.state.students.list.some(
        (s) => (s?.teacher_rubric_score?.criterion_scores || []).length > 0
      )
    },

    // Insights → standard view: exit immediately for instant click feedback,
    // then load the student. The response panel shows its own spinner during fetch.
    openStudentFromInsights(studentId) {
      Alpine.store('insightsMode').exit()
      this.selectStudent(studentId)
    },

    // Tailwind class fragments per rubric band. Expanded Insights buckets use
    // explicit per-band cell/header styles so adjacent student columns read
    // as distinct groups at a glance.
    bandClass(band, kind = 'bg') {
      const styles = {
        'Got It': {
          bg: 'bg-green-1',
          dot: 'bg-green-1',
        },
        Almost: {
          bg: 'bg-yellow-2',
          dot: 'bg-yellow-2',
        },
        'Not Yet': {
          bg: 'bg-red',
          dot: 'bg-red',
        },
      }
      return styles[band]?.[kind] || ''
    },

    // Copies a list of students' display names to the clipboard with a toast.
    // Shared by Insights buckets and the visible-students filter.
    _copyNamesToClipboard(students) {
      const names = students
        .map((s) => s.first_last_initial || s.name)
        .filter(Boolean)
      if (!names.length) return
      const text = names.join('\n')
      const done = () => {
        this.state.selectedStudent.ui.success = `Copied ${names.length} ${
          names.length === 1 ? 'name' : 'names'
        }`
        setTimeout(() => {
          this.state.selectedStudent.ui.success = null
        }, 2000)
      }
      if (navigator.clipboard?.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(done)
          .catch((err) => {
            console.error('Clipboard write failed:', err)
          })
      } else {
        console.warn('navigator.clipboard unavailable; names:', text)
      }
    },

    // Copies names from one Insights bucket. Reads the precomputed bucket.
    copyCriterionBandNames(slug, band) {
      const c = this.rubricInsights.perCriterion[slug]
      this._copyNamesToClipboard((c && c.buckets[band]) || [])
    },

    // Single-pass aggregator for the entire Insights view. Walks the student
    // list once; produces overall counts, per-criterion counts + buckets +
    // struggling flag, and the worst-Not-Yet criterion slug. Replaces the
    // separate distribution / bucket helpers called once per cell.
    get rubricInsights() {
      const list = this.state.students.list
      const total = list.length
      const overall = { 'Got It': 0, Almost: 0, 'Not Yet': 0 }
      const perCriterion = {}
      let scoredStudents = 0
      for (const s of list) {
        const scores = s?.teacher_rubric_score?.criterion_scores || []
        if (scores.length > 0) scoredStudents += 1
        for (const cs of scores) {
          if (overall[cs.score] === undefined) continue
          overall[cs.score] += 1
          let slot = perCriterion[cs.slug]
          if (!slot) {
            slot = perCriterion[cs.slug] = {
              'Got It': 0,
              Almost: 0,
              'Not Yet': 0,
              scored: 0,
              buckets: { 'Got It': [], Almost: [], 'Not Yet': [] },
            }
          }
          slot[cs.score] += 1
          slot.scored += 1
          slot.buckets[cs.score].push(s)
        }
      }
      let worstSlug = null,
        worstCount = 0
      let bestSlug = null,
        bestCount = 0
      for (const slug in perCriterion) {
        const c = perCriterion[slug]
        if (c['Not Yet'] > worstCount) {
          worstSlug = slug
          worstCount = c['Not Yet']
        }
        if (c['Got It'] > bestCount) {
          bestSlug = slug
          bestCount = c['Got It']
        }
      }
      return {
        overall,
        scoredStudents,
        totalStudents: total,
        perCriterion,
        worstSlug,
        worstCount,
        bestSlug,
        bestCount,
      }
    },

    // Helper method to check if a student is typing
    isStudentTyping(studentId) {
      return (
        this.state.typing[studentId] && this.state.typing[studentId].isTyping
      )
    },

    getResponseStatus(studentId, nodeId) {
      const student = this.state.students.list.find((s) => s.id === studentId)
      if (student && student.responses) {
        const numericNodeId = parseInt(nodeId)
        const response = student.responses.find(
          (r) => r.node_id === numericNodeId
        )
        if (response) {
          if (response.is_typing) {
            return 'typing'
          } else if (response.is_submitted) {
            return 'submitted'
          } else {
            return 'draft'
          }
        }
      }
      return 'unknown'
    },

    destroy() {
      if (this.websocketClient) {
        this.websocketClient.disconnect()
      }
      this.stopAIPolling()
    },

    startAIPolling() {
      if (this.aiPollInterval) return
      this.aiPollInterval = setInterval(() => this.fetchAIRubricScores(), 5000)
    },

    stopAIPolling() {
      if (this.aiPollInterval) {
        clearInterval(this.aiPollInterval)
        this.aiPollInterval = null
      }
    },

    fetchAIRubricScores() {
      fetch(`/teach/assignments/${this.state.assignment.id}/ai-rubric-scores/`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          const scores = data.scores || {}
          let anyPending = false
          this.state.students.list.forEach((student) => {
            const score = scores[student.id]
            if (score !== undefined) student.ai_rubric_score = score
            if (student.ai_rubric_score?.status === 'pending') {
              anyPending = true
            }
          })
          if (!anyPending) this.stopAIPolling()
        })
        .catch((error) => {
          console.error('Error polling AI rubric scores:', error)
        })
    },
  }))

  // Student list component (left column).
  // Filter state lives in $store.statusFilter; these helpers are defined
  // here (not on assignmentPage) because nested-Alpine.data parent-scope
  // resolution is unreliable for getters/methods in templates.
  Alpine.data('studentSelection', () => ({
    init() {
      // First non-empty load: auto-select the first student so the teacher
      // lands directly in working state. No filter is applied — teacher
      // picks a slice (Unreviewed, etc.) when ready.
      this.$watch('state.students.list', (newList, oldList) => {
        const wasEmpty = !oldList || oldList.length === 0
        if (!wasEmpty || !newList.length) return
        if (this.state.selectedStudent.id === null) {
          this.selectStudent(newList[0].id)
        }
      })

      // Auto-advance after Return — dispatched by overallGrading.
      this._advanceHandler = () => this.advanceToNextVisible()
      window.addEventListener('assignment:advance', this._advanceHandler)
    },

    destroy() {
      window.removeEventListener('assignment:advance', this._advanceHandler)
    },

    // Workflow state per student — delegates to the module-level helper.
    statusFor(student) {
      return statusFor(student)
    },
    isReviewed(student) {
      return isReviewed(student)
    },

    // Picks the next student after the current one in visibleStudents,
    // wrapping. If the current student just left the filter (e.g., after
    // Return), picks the student now in their old slot. No-op if nothing
    // visible remains.
    advanceToNextVisible() {
      const currentId = this.state.selectedStudent.id
      const visible = this.visibleStudents
      if (!visible.length) return
      const idx = visible.findIndex((s) => s.id === currentId)
      const nextIdx = idx === -1 ? 0 : (idx + 1) % visible.length
      const nextId = visible[nextIdx].id
      if (nextId !== currentId) this.selectStudent(nextId)
    },

    matchesStatusFilter(student) {
      const v = Alpine.store('statusFilter').value
      return v === 'all' || this.statusFor(student) === v
    },

    // Visible cards after the status filter is applied.
    get visibleStudents() {
      return this.state.students.list.filter((s) => this.matchesStatusFilter(s))
    },

    statusCounts() {
      const counts = {
        all: 0,
        unsubmitted: 0,
        unreviewed: 0,
        reviewed: 0,
        returned: 0,
      }
      for (const s of this.state.students.list) {
        counts.all += 1
        counts[this.statusFor(s)] += 1
      }
      return counts
    },

    // Copies the names of currently-visible students (filtered by status).
    copyVisibleStudentNames() {
      this._copyNamesToClipboard(this.visibleStudents)
    },

    // Clicking a student name always lands the teacher on that student —
    // exit insights mode if it's active so the response panel surfaces.
    selectStudentFromList(studentId) {
      Alpine.store('insightsMode').exit()
      this.selectStudent(studentId)
    },

    // Focus-mode avatar rail mirrors the same filter pipeline as the full
    // list so clicking a filter segment narrows the rail too.
    railStudents() {
      return this.visibleStudents
    },

    railTitle(student) {
      const label =
        {
          unsubmitted: 'Not turned in',
          unreviewed: 'Unreviewed',
          reviewed: 'Reviewed',
          returned: 'Returned',
        }[this.statusFor(student)] || ''
      return `${student.first_last_initial} — ${label}`
    },

    getStudentById(studentId) {
      return this.state.students.list.find((s) => s.id === studentId)
    },
    toggleSelectAll() {
      this.state.students.selectAll = !this.state.students.selectAll
      // Only select what's visible AND returnable (submitted, non-missing,
      // matching the active status filter); clear the rest so hidden
      // students can't be returned by accident.
      this.state.students.list.forEach((student) => {
        const returnable = !student.is_missing && student.is_submitted
        const visible = this.matchesStatusFilter(student)
        student.selected =
          returnable && visible ? this.state.students.selectAll : false
      })
    },

    get selectedCount() {
      return this.state.students.list.filter((s) => s.selected).length
    },

    clearSelection() {
      this.state.students.selectAll = false
      this.state.students.list.forEach((s) => {
        s.selected = false
      })
    },

    ungradeAssignment(studentId) {
      // Optimistically update the UI
      this.state.students.list.find((s) => s.id === studentId).is_graded = false

      fetch(`/api/assignments/${this.state.assignment.id}/unreturn/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({ assignment_ids: [studentId] }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            this.state.selectedStudent.ui.success = 'Assignment unreturned'
            setTimeout(() => {
              this.state.selectedStudent.ui.success = null
            }, 3000)
          }
        })
        .catch((error) => {
          console.error('Error unreturning assignment:', error)
          // Revert the optimistic update on error
          this.state.students.list.find(
            (s) => s.id === studentId
          ).is_graded = true
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to unreturn assignment'
          }
        })
    },

    // Return all selected assignments
    returnAssignments() {
      const assignment_ids = this.state.students.list
        .filter((s) => s.selected)
        .map((s) => s.id)
      for (const assignment_id of assignment_ids) {
        this.state.students.list.find(
          (s) => s.id === assignment_id
        ).is_graded = true
      }
      fetch(`/api/assignments/${this.state.assignment.id}/return/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({ assignment_ids: assignment_ids }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            data.assignment_ids.forEach((assignment_id) => {
              this.state.students.list.find(
                (s) => s.id === assignment_id
              ).is_graded = true
            })
            this.state.selectedStudent.ui.success =
              'Assignments returned to students'
            setTimeout(() => {
              this.state.selectedStudent.ui.success = null
            }, 3000)
          }
        })
        .catch((error) => {
          console.error('Error returning assignments:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error = 'Failed to return assignments'
          }
        })
    },

    // Excuse or unexcuse an assignment
    excuseAssignment(studentId) {
      fetch(`/api/student-assignments/${studentId}/excuse/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to excuse this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            const student = this.getStudentById(studentId)
            if (student) {
              // Use the value from the server response instead of toggling
              student.is_excused = !student.is_excused
              this.state.selectedStudent.ui.success = student.is_excused
                ? 'Assignment excused'
                : 'Assignment unexcused'
              setTimeout(() => {
                this.state.selectedStudent.ui.success = null
              }, 3000)
            }
          }
        })
        .catch((error) => {
          console.error('Error updating excuse status:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to update excuse status'
          }
        })
    },

    // Return a single assignment
    retakeAssignment(studentId) {
      fetch(`/api/student-assignments/${studentId}/retake/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            // Update UI for the correct student using passed studentId
            const student = this.getStudentById(studentId)
            if (student) {
              student.is_retake = !student.is_retake
              student.is_submitted = !student.is_submitted
              this.state.selectedStudent.ui.success = 'Assignment reassigned'
              setTimeout(() => {
                this.state.selectedStudent.ui.success = null
              }, 3000)
            }
          }
        })
        .catch((error) => {
          console.error('Error returning assignment:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error = 'Failed to return assignment'
          }
        })
    },
  }))

  // Student responses component (middle column).
  Alpine.data('responseGrading', () => ({
    getResponseById(responseId) {
      const response = this.currentStudent.responses.find(
        (r) => r.id === responseId
      )
      if (!response) {
        console.error('Response not found:', responseId)
      }
      return response
    },

    updateStudentScore(totalScore) {
      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === this.state.selectedStudent.id
      )
      if (studentIndex !== -1) {
        this.state.students.list[studentIndex].score = totalScore
        this.state.students.list[studentIndex].is_saved = true
      }
    },

    calculateTotalScore() {
      if (!this.currentStudent.responses) return 0

      return this.currentStudent.responses.reduce((sum, response) => {
        return sum + (Number(response.score) || 0)
      }, 0)
    },

    showSuccess(message) {
      this.state.selectedStudent.ui.error = null
      this.state.selectedStudent.ui.success = message
      setTimeout(() => {
        this.state.selectedStudent.ui.success = null
      }, 3000)
    },

    showError(message) {
      this.state.selectedStudent.ui.success = null
      this.state.selectedStudent.ui.error = message
    },
    postFeedbackToAPI(responseId, feedback, score) {
      return fetch(`/api/responses/${responseId}/grade/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({ feedback, score, is_saved: true }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .catch((error) => {
          console.error('Error updating feedback:', error)
          this.showError(error.message || 'Failed to save feedback')
          throw error // Re-throw to be caught by the caller
        })
    },

    // Combined workflow methods
    updateFeedback(responseId) {
      const response = this.getResponseById(responseId)
      if (!response) return

      const feedback = response.feedback || ''
      const score = response.score || 0

      this.postFeedbackToAPI(responseId, feedback, score)
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          }

          const isLiveAssignment = !this.currentStudent.is_submitted
          this.showSuccess(
            isLiveAssignment
              ? 'Feedback saved and visible to student'
              : 'Feedback saved'
          )

          // Only update overall score for by_question grading type
          if (this.state.assignment.grading_type === 'by_question') {
            const totalScore = this.calculateTotalScore()
            this.updateStudentScore(totalScore)
          }
        })
        .catch((error) => {
          console.error('Error updating feedback:', error)
          this.showError(error.message || 'Failed to save feedback')
        })
    },
  }))

  // Grading component (right column). PM assignments get a Scoring/Status
  // tab pair; non-PM falls through to the legacy panel.
  Alpine.data('overallGrading', (config = {}) => ({
    teacherAssistant: {
      // 'scoring' | 'status' | null (non-PM, no tabs)
      activeTab: config.is_progress_monitoring ? 'scoring' : null,
      csrf_token: config.csrf_token || null,
      teacherRubricScores: {}, // { [criterion_slug]: "Got It" | "Almost" | "Not Yet" }
    },
    // Unified Save state. `dirty` flips true on any local edit (chip click,
    // feedback text, score input). saveAll() commits everything; Return
    // is gated so we never lose unsaved work.
    dirty: false,
    saving: false,
    pendingStudentId: null,
    savedSnapshot: null,

    init() {
      this.$watch('state.selectedStudent.id', () => {
        this.dirty = false
        this.pendingStudentId = null
        if (this.currentStudent?.loaded) this.syncFromCurrentStudent()
      })
      this._studentLoadedHandler = (event) => {
        if (
          event.detail.studentId === this.state.selectedStudent.id &&
          !this.dirty
        ) {
          this.syncFromCurrentStudent()
        }
      }
      window.addEventListener(
        'assignment:student-loaded',
        this._studentLoadedHandler
      )
      window.assignmentGradingGuard = {
        requestNavigation: (studentId) => this.requestNavigation(studentId),
      }
      if (this.currentStudent?.loaded) this.syncFromCurrentStudent()
      // Cmd/Ctrl+S from anywhere on the page commits the unified Save.
      this._saveHandler = () => {
        if (this.dirty && !this.saving) this.saveAll()
      }
      window.addEventListener('assignment:save', this._saveHandler)
    },

    destroy() {
      window.removeEventListener('assignment:save', this._saveHandler)
      window.removeEventListener(
        'assignment:student-loaded',
        this._studentLoadedHandler
      )
      delete window.assignmentGradingGuard
    },

    syncFromCurrentStudent() {
      this.initTeacherRubricScores()
      this.savedSnapshot = buildGradeSnapshot(
        this.currentStudent,
        this.teacherAssistant.teacherRubricScores
      )
    },

    requestNavigation(studentId) {
      const disposition = navigationDisposition({
        currentStudentId: this.state.selectedStudent.id,
        targetStudentId: studentId,
        dirty: this.dirty,
        saving: this.saving,
      })
      if (disposition === 'same' || disposition === 'blocked') return false
      if (disposition === 'confirm') {
        this.pendingStudentId = studentId
        return false
      }
      return true
    },

    cancelPendingNavigation() {
      this.pendingStudentId = null
    },

    discardAndNavigate() {
      const targetStudentId = this.pendingStudentId
      if (!targetStudentId) return
      const student = this.savedSnapshot
        ? this.state.students.list.find(
            (entry) => entry.id === this.savedSnapshot.studentId
          )
        : null
      if (student && this.savedSnapshot) {
        student.score = this.savedSnapshot.score
        student.feedback = this.savedSnapshot.feedback
        student.is_complete = this.savedSnapshot.isComplete ? 1 : 0
        student.loaded = false
      }
      this.teacherAssistant.teacherRubricScores = this.savedSnapshot
        ? { ...this.savedSnapshot.rubricScores }
        : {}
      this.dirty = false
      this.pendingStudentId = null
      this.forceNavigation(targetStudentId)
    },

    async saveAndNavigate() {
      const targetStudentId = this.pendingStudentId
      if (!targetStudentId) return
      const saved = await this.saveAll({ advanceAfterReview: false })
      if (!saved) return
      this.pendingStudentId = null
      this.forceNavigation(targetStudentId)
    },

    forceNavigation(studentId) {
      window.dispatchEvent(
        new CustomEvent('assignment:force-navigation', {
          detail: { studentId },
        })
      )
    },

    initTeacherRubricScores() {
      const scores = {}
      const cs =
        this.currentStudent?.teacher_rubric_score?.criterion_scores || []
      for (const entry of cs) {
        scores[entry.slug] = entry.score
      }
      this.teacherAssistant.teacherRubricScores = scores
    },

    // Chips no longer auto-save — they stage locally and mark dirty.
    setRubricScore(slug, score) {
      // Clicking the selected score clears it, showing any AI suggestion again.
      if (this.teacherAssistant.teacherRubricScores[slug] === score) {
        delete this.teacherAssistant.teacherRubricScores[slug]
      } else {
        this.teacherAssistant.teacherRubricScores[slug] = score
      }
      this.dirty = true
    },

    get aiScoreStatus() {
      return this.currentStudent?.ai_rubric_score?.status || null
    },

    get aiScoreError() {
      return this.currentStudent?.ai_rubric_score?.error
    },

    _aiCriterionScore(slug) {
      const aiScore = this.currentStudent?.ai_rubric_score
      if (aiScore?.status !== 'complete') return null
      return (aiScore.criterion_scores || []).find((cs) => cs.slug === slug)
    },

    getAIRubricScore(slug) {
      return this._aiCriterionScore(slug)?.score || null
    },

    getAIRubricJustification(slug) {
      return this._aiCriterionScore(slug)?.justification || ''
    },

    // The AI score shows as a dashed chip unless the teacher picked the same
    // score, so a disagreement stays visible next to the teacher's pick.
    isSuggested(slug) {
      const aiScore = this.getAIRubricScore(slug)
      return (
        !!aiScore && this.teacherAssistant.teacherRubricScores[slug] !== aiScore
      )
    },

    get hasVisibleSuggestion() {
      const aiScores = this.currentStudent?.ai_rubric_score?.criterion_scores
      return (aiScores || []).some((cs) => this.isSuggested(cs.slug))
    },

    retryAIRubricScore() {
      const studentId = this.currentStudent.id
      fetch(`/teach/ai-rubric-score/${studentId}/retry/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.teacherAssistant.csrf_token,
        },
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) throw new Error(data.error || 'Failed to retry AI scoring')
          const student = this.state.students.list.find(
            (s) => s.id === studentId
          )
          student.ai_rubric_score = {
            status: 'pending',
            error: '',
            criterion_scores: [],
          }
          this.startAIPolling()
        })
        .catch((error) => {
          console.error('Error retrying AI rubric score:', error)
          this.showError(error.message || 'Failed to retry AI scoring')
        })
    },

    // Called from textarea / score-input @input handlers.
    markDirty() {
      this.dirty = true
    },

    // Workflow state for the current student — delegates to the module helper.
    get statusForCurrent() {
      return statusFor(this.currentStudent)
    },
    isReviewed(student) {
      return isReviewed(student)
    },

    // Unified commit: rubric chips (PM only) + overall feedback + score.
    // Runs the rubric POST first so a failure there doesn't leave the
    // overall feedback in a confusingly half-saved state.
    async saveAll({ advanceAfterReview = true } = {}) {
      if (this.saving || !this.currentStudent?.id) return false
      const snapshot = buildGradeSnapshot(
        this.currentStudent,
        this.teacherAssistant.teacherRubricScores
      )
      // Distinguish "Save changes" from "Mark reviewed" (clean + not yet
      // reviewed) — the latter is a pure workflow move and should
      // auto-advance like Return.
      const wasMarkReviewed =
        !this.dirty && !this.isReviewed(this.currentStudent)
      this.saving = true
      let shouldAdvance = false
      try {
        if (this.teacherAssistant.activeTab !== null) {
          await this.saveTeacherRubricScores(snapshot)
        }
        await this.saveOverallFeedbackPromise(snapshot)
        this.savedSnapshot = snapshot
        this.dirty = false
        this.showSuccess(wasMarkReviewed ? 'Marked as reviewed' : 'Saved')
        shouldAdvance = wasMarkReviewed && advanceAfterReview
      } catch (err) {
        console.error('Save failed:', err)
        // Individual handlers already surfaced an error message.
        return false
      } finally {
        this.saving = false
      }
      if (shouldAdvance) {
        window.dispatchEvent(new CustomEvent('assignment:advance'))
      }
      return true
    },

    // POSTs the full criterion-scores map in one request.
    saveTeacherRubricScores(snapshot) {
      return fetch(`/teach/rubric-score/${snapshot.studentId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.teacherAssistant.csrf_token,
        },
        body: JSON.stringify({ criterion_scores: snapshot.rubricScores }),
      })
        .then((r) => {
          if (!r.ok) throw new Error(`Rubric save HTTP ${r.status}`)
          return r.json()
        })
        .then((data) => {
          if (data.teacher_rubric_score) {
            const idx = this.state.students.list.findIndex(
              (s) => s.id === snapshot.studentId
            )
            if (idx !== -1) {
              this.state.students.list[idx].teacher_rubric_score =
                data.teacher_rubric_score
            }
          }
        })
        .catch((err) => {
          this.showError('Failed to save rubric scores.')
          throw err
        })
    },

    showSuccess(message) {
      this.state.selectedStudent.ui.error = null
      this.state.selectedStudent.ui.success = message
      setTimeout(() => {
        this.state.selectedStudent.ui.success = null
      }, 3000)
    },

    showError(message) {
      this.state.selectedStudent.ui.success = null
      this.state.selectedStudent.ui.error = message
    },
    postGradeToAPI(studentId, score, feedback, isComplete) {
      return fetch(`/api/student-assignments/${studentId}/grade/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({
          score: score,
          feedback: feedback,
          is_complete: isComplete,
        }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .catch((error) => {
          console.error('Error grading assignment:', error)
          this.showError(error.message || 'Failed to save feedback')
          throw error // Re-throw to be caught by the caller
        })
    },

    // Promise variant used by saveAll() so it can sequence rubric + overall
    // and surface a single combined "Saved" toast.
    saveOverallFeedbackPromise(snapshot) {
      return this.postGradeToAPI(
        snapshot.studentId,
        snapshot.score,
        snapshot.feedback,
        snapshot.isComplete
      ).then((data) => {
        if (data?.error) throw new Error(data.error)
        const student = this.state.students.list.find(
          (entry) => entry.id === snapshot.studentId
        )
        if (student) student.is_saved = true
      })
    },

    get submissionDate() {
      return new Date(this.currentStudent.submitted_at).toLocaleString(
        'en-US',
        {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }
      )
    },

    get returnedDate() {
      return new Date(this.currentStudent.returned_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    },

    toggleReturnAssignment() {
      const studentId = this.currentStudent.id
      const isCurrentlyGraded = this.currentStudent.is_graded
      const endpoint = isCurrentlyGraded
        ? `/api/assignments/${this.state.assignment.id}/unreturn/`
        : `/api/assignments/${this.state.assignment.id}/return/`

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({
          assignment_ids: [studentId],
        }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assignment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            // Update the student's graded status
            const student = this.state.students.list.find(
              (s) => s.id === studentId
            )
            if (student) {
              student.is_graded = !isCurrentlyGraded
            }
            this.state.selectedStudent.ui.success = isCurrentlyGraded
              ? 'Assignment unreturned'
              : 'Assignment returned to student'
            setTimeout(() => {
              this.state.selectedStudent.ui.success = null
            }, 3000)
            // Advance only on Return (not Unreturn) so the teacher
            // bops through the queue without a manual next-click.
            if (!isCurrentlyGraded) {
              window.dispatchEvent(new CustomEvent('assignment:advance'))
            }
          }
        })
        .catch((error) => {
          console.error('Error toggling assignment return status:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to update assignment status'
          }
        })
    },

    get assignmentStatus() {
      const student = this.currentStudent
      if (student.is_missing) {
        return 'Missing'
      }
      if (student.is_excused) {
        return 'Excused'
      }
      if (student.is_retake) {
        return 'Retake'
      }
      if (
        student.is_graded &&
        this.state.assignment.grading_type === 'ungraded'
      ) {
        return 'Returned'
      }
      if (student.is_graded) {
        return 'Graded and Returned'
      }
      if (
        student.is_saved &&
        this.state.assignment.grading_type !== 'ungraded'
      ) {
        return 'Graded'
      }
      return 'Submitted'
    },
  }))

  // Global cleanup on page unload
  window.addEventListener('beforeunload', function () {
    if (window.assignmentWebSocket) {
      window.assignmentWebSocket.disconnect()
    }
  })
})
